import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import SecureStorage from 'utils/secureStorage';
import { ImportSessionStorage } from 'utils/importSessionStorage';

// CSRF tokens stored in memory only (Redux state) for security
// Not in localStorage - prevents XSS attacks from stealing CSRF tokens

// Feature flag for cookie authentication with safe parsing
let USE_COOKIE_AUTH = false;
try {
  USE_COOKIE_AUTH = process.env.REACT_APP_USE_COOKIE_AUTH === 'true';
} catch (error) {
  console.error('Error parsing USE_COOKIE_AUTH:', error);
  USE_COOKIE_AUTH = false;
}

interface UserData {
  id: number;
  email?: string; // Optional - not stored in localStorage (security: no PII client-side)
  username: string;
  roles?: string[];
}

interface AuthState {
  token: string | null;
  user: UserData | null;
  csrfToken: string | null;
}

const initialState: AuthState = {
  token: USE_COOKIE_AUTH ? null : SecureStorage.getAuthToken(),
  user: SecureStorage.getUserData(),
  csrfToken: null, // CSRF tokens stored in memory only - backend provides fresh token after page load
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(state, action: PayloadAction<{ token: string; user?: UserData; csrfToken?: string; isImpersonating?: boolean }>) {
      // Check if this is an impersonation session from action payload
      const isImpersonating = action.payload.isImpersonating === true;

      if (USE_COOKIE_AUTH && !isImpersonating) {
        // Cookie mode: Don't store token in localStorage
        state.token = null; // Token is in HttpOnly cookie
        state.csrfToken = action.payload.csrfToken || null;

        if (process.env.NODE_ENV === 'development') {
          console.log('🍪 Cookie auth mode: Token stored in HttpOnly cookie');
        }
      } else if (isImpersonating) {
        // Impersonation mode: Store token in state even with cookie auth
        // This allows the impersonation token to be sent as Bearer header
        // while keeping the admin's cookie intact for session restoration
        state.token = action.payload.token;
        state.csrfToken = action.payload.csrfToken || null;

        if (process.env.NODE_ENV === 'development') {
          console.log('🎭 Impersonation mode: Token stored in Redux state:', !!action.payload.token);
        }
      } else {
        // Legacy mode: Store in localStorage
        state.token = action.payload.token;
        SecureStorage.setAuthToken(action.payload.token);

        if (action.payload.csrfToken) {
          state.csrfToken = action.payload.csrfToken;
          // CSRF token stored in memory only (Redux state) for security
          // Not persisted to localStorage - prevents XSS attacks
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('📦 LocalStorage auth mode: Token stored in localStorage');
        }
      }

      // Store user data securely with PII protection
      if (action.payload.user) {
        state.user = action.payload.user;
        SecureStorage.setUserData(action.payload.user);
      }
    },
    logout(state) {
      // Was there actually a session to end? Read before the nulls below.
      const hadSession = !!(state.token || state.user);

      state.token = null;
      state.user = null;
      state.csrfToken = null;

      // Use SecureStorage for consistent cleanup (also removes legacy encrypted data)
      SecureStorage.clearUserData();
      SecureStorage.clearAuthToken();

      // No post-auth destination may outlive the session that created it.
      // Cleared here rather than in the sign-out handlers so every path gets
      // it: both sign-out menus, token expiry, the 401 handler and the
      // suspension handler all dispatch this reducer. Without it, a stale
      // deep link could deliver the next user in the same tab to the previous
      // user's destination.
      //
      // Gated on hadSession because this reducer also fires for an ALREADY
      // signed-out visitor: ProtectedRoute renders its children once before
      // the redirect effect runs, those children fire authenticated requests,
      // and baseQuery dispatches logout on the resulting 401s. Clearing
      // unconditionally would wipe the deep link ProtectedRoute had just
      // stored, on every single deep-link visit.
      if (hadSession) {
        ImportSessionStorage.clearReturnTo();
      }

      // CSRF token cleared from Redux state (line above: state.csrfToken = null)
      // No localStorage removal needed - never stored there
    },
  },
});

export const { loginSuccess, logout } = authSlice.actions;
export default authSlice.reducer;
